import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  ExternalLink,
  Target,
  CheckCircle,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Package,
  Folder,
  Camera,
  Video,
  BookOpen,
  ArrowUpRight,
  ArrowDown,
} from "lucide-react";
import { productService } from "../../lib/productService";
import { toast } from "sonner";
import { AffiliateHeaderContext } from "../../context/AffiliateHeaderContext";

const ProductDetailPage = () => {
  const [productDetails, setProductDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { affiliateName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const setHeaderContent = useContext(AffiliateHeaderContext);

  // Get product ID from navigation state or extract from URL if needed
  const productId = location.state?.productId;

  // Set header content
  useEffect(() => {
    if (productDetails?.product) {
      setHeaderContent(
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            <div className="w-2 h-8 bg-gradient-to-b from-[#B8460E] to-[#8B3A0E] rounded-full"></div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {productDetails.product.name}
              </h1>
              <p className="text-gray-400 text-sm">
                Product Details & Marketing Materials
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/affiliate/${affiliateName}/products`)}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors group bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg border border-white/20"
          >
            <Package className="w-4 h-4" />
            Back to Products
          </button>
        </div>
      );
    }
    return () => setHeaderContent(null);
  }, [setHeaderContent, productDetails, affiliateName, navigate]);

  const fetchProductDetails = useCallback(async () => {
    setLoading(true);
    const result = await productService.getProductDetails(productId);
    if (result.success) {
      console.log("🔍 Product Details Data:", result.data);
      console.log("🔍 Marketing Content:", result.data.marketing_content);
      console.log(
        "🔍 Top Image Ads:",
        result.data.marketing_content?.top_performing_examples?.top_image_ads
      );
      console.log(
        "🔍 Videos:",
        result.data.marketing_content?.high_performance_creatives?.videos
      );
      setProductDetails(result.data);
    } else {
      toast.error("Failed to fetch product details");
      navigate(`/affiliate/${affiliateName}/products`);
    }
    setLoading(false);
  }, [productId, navigate, affiliateName]);

  useEffect(() => {
    if (!productId) {
      toast.error("Product not found");
      navigate(`/affiliate/${affiliateName}/products`);
      return;
    }

    fetchProductDetails();
  }, [productId, fetchProductDetails, affiliateName, navigate]);

  // Define allImages safely
  const product = productDetails?.product;
  const allImages = product
    ? [product.main_image_url, ...(product.additional_images || [])].filter(
        Boolean
      )
    : [];

  const nextImage = () => {
    if (allImages.length > 1) {
      setSelectedImageIndex((prev) => (prev + 1) % allImages.length);
    }
  };

  const previousImage = () => {
    if (allImages.length > 1) {
      setSelectedImageIndex(
        (prev) => (prev - 1 + allImages.length) % allImages.length
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
          <p className="text-white">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!productDetails) {
    return (
      <div className="space-y-6">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center">
          <p className="text-white">Product not found</p>
          <button
            onClick={() => navigate(`/affiliate/${affiliateName}/products`)}
            className="mt-4 bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white px-4 py-2 rounded-lg transition-all duration-300"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Main Product Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Side - Image Gallery */}
        <div className="space-y-4">
          {/* Main Image Display */}
          <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden aspect-square">
            {allImages.length > 0 ? (
              <>
                <img
                  src={allImages[selectedImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />

                {/* Navigation Arrows */}
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={previousImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-200"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-200"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                {allImages.length > 1 && (
                  <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {selectedImageIndex + 1} / {allImages.length}
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500">
                <ImageIcon className="w-24 h-24" />
              </div>
            )}
          </div>

          {/* Thumbnail Strip */}
          {allImages.length > 1 && (
            <div className="grid grid-cols-4 gap-3">
              {allImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                    index === selectedImageIndex
                      ? "border-[#B8460E] shadow-lg shadow-[#B8460E]/20"
                      : "border-white/20 hover:border-white/40"
                  }`}
                >
                  <img
                    src={image}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Side - Product Info */}
        <div className="space-y-6">
          {/* Product Title & Description */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 space-y-4">
            <h1 className="text-3xl font-bold text-white">{product.name}</h1>
            <p className="text-gray-300 leading-relaxed">
              {product.description}
            </p>
          </div>

          {/* Asset Links */}
          {productDetails.links && productDetails.links.length > 0 && (
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-[#B8460E]" />
                Asset Links
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {productDetails.links.map((link, index) => (
                  <a
                    key={link.id || index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {link.button_text}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Assets Section - Only show if configured */}
      {productDetails?.marketing_content?.product_assets &&
        (productDetails.marketing_content.product_assets.product_mockups_url ||
          productDetails.marketing_content.product_assets
            .training_documents_url ||
          productDetails.marketing_content.product_assets
            .raw_ugc_content_enabled) && (
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Folder className="w-6 h-6 text-yellow-400" />
                <h3 className="text-2xl font-semibold text-white">
                  Product Assets
                </h3>
              </div>
              <p className="text-gray-400 text-sm">
                Download everything you need
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Product Mockups Card */}
              {productDetails.marketing_content.product_assets
                .product_mockups_url && (
                <a
                  href={
                    productDetails.marketing_content.product_assets
                      .product_mockups_url
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-6 hover:bg-white/10 hover:border-[#B8460E]/50 hover:shadow-lg hover:shadow-[#B8460E]/20 transition-all duration-300 cursor-pointer group overflow-hidden"
                >
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>

                  <div className="relative z-10 text-center space-y-4">
                    <div className="w-16 h-16 bg-[#B8460E]/20 rounded-full flex items-center justify-center mx-auto group-hover:bg-[#B8460E]/30 group-hover:scale-110 transition-all duration-300">
                      <Camera className="w-8 h-8 text-[#B8460E]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-[#B8460E] transition-colors">
                        Product Mockups
                      </h4>
                      <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">
                        High-quality product images
                      </p>
                    </div>
                    <div className="flex items-center justify-center">
                      <ArrowUpRight className="w-4 h-4 text-[#B8460E] group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" />
                    </div>
                  </div>
                </a>
              )}

              {/* Raw UGC Content Card */}
              {productDetails.marketing_content.product_assets
                .raw_ugc_content_enabled && (
                <div className="relative bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-6 hover:bg-white/10 hover:border-[#B8460E]/50 hover:shadow-lg hover:shadow-[#B8460E]/20 transition-all duration-300 group overflow-hidden">
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>

                  <div className="relative z-10 text-center space-y-4">
                    <div className="w-16 h-16 bg-[#B8460E]/20 rounded-full flex items-center justify-center mx-auto group-hover:bg-[#B8460E]/30 group-hover:scale-110 transition-all duration-300">
                      <Video className="w-8 h-8 text-[#B8460E]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-[#B8460E] transition-colors">
                        Raw UGC Content
                      </h4>
                      <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">
                        See UGC Library section below
                      </p>
                    </div>
                    <div className="flex items-center justify-center">
                      <ArrowDown className="w-4 h-4 text-[#B8460E] group-hover:scale-110 group-hover:animate-bounce transition-all duration-300" />
                    </div>
                  </div>
                </div>
              )}

              {/* Training Documents Card */}
              {productDetails.marketing_content.product_assets
                .training_documents_url && (
                <a
                  href={
                    productDetails.marketing_content.product_assets
                      .training_documents_url
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-6 hover:bg-white/10 hover:border-[#B8460E]/50 hover:shadow-lg hover:shadow-[#B8460E]/20 transition-all duration-300 cursor-pointer group overflow-hidden"
                >
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>

                  <div className="relative z-10 text-center space-y-4">
                    <div className="w-16 h-16 bg-[#B8460E]/20 rounded-full flex items-center justify-center mx-auto group-hover:bg-[#B8460E]/30 group-hover:scale-110 transition-all duration-300">
                      <BookOpen className="w-8 h-8 text-[#B8460E]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-[#B8460E] transition-colors">
                        Training Documents
                      </h4>
                      <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">
                        Product benefits and brand guidelines
                      </p>
                    </div>
                    <div className="flex items-center justify-center">
                      <ArrowUpRight className="w-4 h-4 text-[#B8460E] group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" />
                    </div>
                  </div>
                </a>
              )}
            </div>
          </div>
        )}

      {/* Top Performing Examples Section - Only show if configured */}
      {productDetails?.marketing_content?.top_performing_examples &&
        (productDetails.marketing_content.top_performing_examples.top_image_ads
          ?.length > 0 ||
          productDetails.marketing_content.top_performing_examples
            .image_library_url ||
          productDetails.marketing_content.top_performing_examples
            .video_ads_enabled) && (
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-white">
                Top Performing Examples
              </h3>
              {productDetails.marketing_content.top_performing_examples
                .image_library_url && (
                <a
                  href={
                    productDetails.marketing_content.top_performing_examples
                      .image_library_url
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#B8460E] hover:text-[#8B3A0E] text-sm font-medium transition-colors"
                >
                  Learn from what's working →
                </a>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Top 3 Image Ads Column */}
              {productDetails.marketing_content.top_performing_examples
                .top_image_ads?.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <ImageIcon className="w-5 h-5 text-[#B8460E]" />
                    <h4 className="text-lg font-semibold text-white">
                      Top{" "}
                      {
                        productDetails.marketing_content.top_performing_examples
                          .top_image_ads.length
                      }{" "}
                      Image Ads
                    </h4>
                  </div>

                  <div className="space-y-4">
                    {productDetails.marketing_content.top_performing_examples.top_image_ads.map(
                      (ad, index) => (
                        <div
                          key={index}
                          className="relative bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4 hover:bg-white/10 hover:border-[#B8460E]/50 hover:shadow-lg hover:shadow-[#B8460E]/20 transition-all duration-300 group overflow-hidden"
                        >
                          {/* Shine effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>
                          <div className="relative z-10 space-y-3">
                            <h5 className="text-lg font-bold text-yellow-400">
                              {ad.title || `Ad ${index + 1}`}
                            </h5>

                            {/* Actual Ad Creative */}
                            <div className="bg-gray-800 rounded-lg p-3 text-center">
                              {ad.image_url || ad.imageUrl ? (
                                <img
                                  src={ad.image_url || ad.imageUrl}
                                  alt={
                                    ad.title || `Top performing ad ${index + 1}`
                                  }
                                  className="w-full h-auto rounded-lg"
                                  onError={(e) => {
                                    console.error(
                                      "🚨 Image failed to load:",
                                      ad.image_url || ad.imageUrl,
                                      ad
                                    );
                                    e.target.style.display = "none";
                                  }}
                                  onLoad={() =>
                                    console.log(
                                      "✅ Image loaded:",
                                      ad.image_url || ad.imageUrl
                                    )
                                  }
                                />
                              ) : (
                                <div className="w-full h-48 bg-gray-700 rounded-lg flex items-center justify-center text-gray-400">
                                  <span>No image available</span>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-300">
                                Spend:{" "}
                                <span className="text-white font-semibold">
                                  ${ad.spend || "0"}
                                </span>
                              </span>
                              <span className="text-gray-300">
                                ROAS:{" "}
                                <span className="text-white font-semibold">
                                  {ad.roas || "0"}x
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* View Full Image Library Button - Outside the cards */}
                  {productDetails.marketing_content.top_performing_examples
                    .image_library_url && (
                    <div className="mt-4">
                      <a
                        href={
                          productDetails.marketing_content
                            .top_performing_examples.image_library_url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-[#B8460E] hover:bg-[#8B3A0E] text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                      >
                        <Camera className="w-4 h-4" />
                        View Full Image Library
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Top 3 Video Ads Column */}
              {productDetails.marketing_content.top_performing_examples
                .top_video_ads?.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Video className="w-5 h-5 text-[#B8460E]" />
                    <h4 className="text-lg font-semibold text-white">
                      Top{" "}
                      {
                        productDetails.marketing_content.top_performing_examples
                          .top_video_ads.length
                      }{" "}
                      Video Ads
                    </h4>
                  </div>

                  <div className="space-y-4">
                    {productDetails.marketing_content.top_performing_examples.top_video_ads.map(
                      (ad, index) => (
                        <div
                          key={index}
                          className="relative bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4 hover:bg-white/10 hover:border-[#B8460E]/50 hover:shadow-lg hover:shadow-[#B8460E]/20 transition-all duration-300 group overflow-hidden"
                        >
                          {/* Shine effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>
                          <div className="relative z-10 space-y-3">
                            <h5 className="text-lg font-bold text-yellow-400">
                              {ad.title || `Video Ad ${index + 1}`}
                            </h5>

                            {/* Video Player */}
                            <div className="bg-gray-800 rounded-lg overflow-hidden">
                              {ad.video_url || ad.videoUrl ? (
                                <video
                                  className="w-full h-auto"
                                  controls
                                  preload="metadata"
                                  onError={(e) => {
                                    console.error(
                                      "🚨 Video failed to load:",
                                      ad.video_url || ad.videoUrl,
                                      ad
                                    );
                                  }}
                                  onLoadedData={() =>
                                    console.log(
                                      "✅ Video loaded:",
                                      ad.video_url || ad.videoUrl
                                    )
                                  }
                                >
                                  <source
                                    src={ad.video_url || ad.videoUrl}
                                    type="video/mp4"
                                  />
                                  Your browser does not support the video tag.
                                </video>
                              ) : (
                                <div className="w-full h-48 bg-gray-700 rounded-lg flex items-center justify-center text-gray-400">
                                  <span>No video available</span>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-300">
                                Spend:{" "}
                                <span className="text-white font-semibold">
                                  ${ad.spend || "0"}
                                </span>
                              </span>
                              <span className="text-gray-300">
                                ROAS:{" "}
                                <span className="text-white font-semibold">
                                  {ad.roas || "0"}x
                                </span>
                              </span>
                            </div>

                            {/* View Full Video Library Button - only show on last item */}
                            {index ===
                              productDetails.marketing_content
                                .top_performing_examples.top_video_ads.length -
                                1 &&
                              productDetails.marketing_content
                                .top_performing_examples.video_library_url && (
                                <a
                                  href={
                                    productDetails.marketing_content
                                      .top_performing_examples.video_library_url
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-full bg-[#B8460E] hover:bg-[#8B3A0E] text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                  <Video className="w-4 h-4" />
                                  View Full Video Library
                                </a>
                              )}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      {/* High Performance Creatives Section - Only show if configured */}
      {productDetails?.marketing_content?.high_performance_creatives &&
        (productDetails.marketing_content.high_performance_creatives.videos
          ?.length > 0 ||
          productDetails.marketing_content.high_performance_creatives
            .creative_library_url) && (
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <div className="mb-6">
              <h3 className="text-2xl font-semibold text-white mb-3">
                High Performance Creatives
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                This is what high performance DTC video ads look like in the
                supplement niche. These companies are doing 10M+ a year and you
                should copy their structure to implement with our products to
                see massive scale.
              </p>
            </div>

            {/* Videos Grid */}
            {productDetails.marketing_content.high_performance_creatives.videos
              ?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {productDetails.marketing_content.high_performance_creatives.videos.map(
                  (video, index) => (
                    <div
                      key={index}
                      className="relative bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4 hover:bg-white/10 hover:border-[#B8460E]/50 hover:shadow-lg hover:shadow-[#B8460E]/20 transition-all duration-300 group overflow-hidden"
                    >
                      {/* Shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>
                      <div className="relative z-10 space-y-3">
                        {/* Video Player - Now at the top */}
                        <div className="bg-gray-800 rounded-lg overflow-hidden">
                          {video.video_url || video.videoUrl ? (
                            <video
                              className="w-full h-auto"
                              controls
                              preload="metadata"
                              onError={(e) => {
                                console.error(
                                  "🚨 Video failed to load:",
                                  video.video_url || video.videoUrl,
                                  video
                                );
                              }}
                              onLoadedData={() =>
                                console.log(
                                  "✅ Video loaded:",
                                  video.video_url || video.videoUrl
                                )
                              }
                            >
                              <source
                                src={video.video_url || video.videoUrl}
                                type="video/mp4"
                              />
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            <div className="w-full h-48 bg-gray-700 rounded-lg flex items-center justify-center text-gray-400">
                              <span>No video available</span>
                            </div>
                          )}
                        </div>

                        {/* Title and Description - Now below the video */}
                        <div className="text-center space-y-2">
                          <h5 className="text-lg font-semibold text-white">
                            {video.title ||
                              `High Performance Creative ${index + 1}`}
                          </h5>
                          <p className="text-gray-400 text-sm">
                            {video.description || "Supplement niche example"}
                          </p>

                          <div className="pt-2">
                            <span className="text-[#B8460E] text-xs font-semibold uppercase tracking-wider">
                              PRODUCTION NOTES:
                            </span>
                            <div className="mt-2 flex flex-wrap gap-1 justify-center">
                              {video.production_notes?.ai_voice && (
                                <span className="bg-[#B8460E]/20 text-[#B8460E] px-2 py-1 rounded text-xs">
                                  • AI voice
                                </span>
                              )}
                              {video.production_notes?.ugc_clips && (
                                <span className="bg-[#B8460E]/20 text-[#B8460E] px-2 py-1 rounded text-xs">
                                  • UGC clips
                                </span>
                              )}
                              {video.production_notes?.b_roll && (
                                <span className="bg-[#B8460E]/20 text-[#B8460E] px-2 py-1 rounded text-xs">
                                  • B-roll
                                </span>
                              )}
                              {video.production_notes?.ai_deep_fake && (
                                <span className="bg-[#B8460E]/20 text-[#B8460E] px-2 py-1 rounded text-xs">
                                  • AI deep fake
                                </span>
                              )}
                              {video.production_notes?.product_b_roll && (
                                <span className="bg-[#B8460E]/20 text-[#B8460E] px-2 py-1 rounded text-xs">
                                  • Product b-roll
                                </span>
                              )}
                              {video.production_notes?.custom_notes?.map(
                                (customNote, noteIndex) =>
                                  customNote.trim() && (
                                    <span
                                      key={noteIndex}
                                      className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs"
                                    >
                                      • {customNote}
                                    </span>
                                  )
                              )}
                              {(!video.production_notes ||
                                Object.values(
                                  video.production_notes || {}
                                ).every((note) =>
                                  Array.isArray(note)
                                    ? note.length === 0
                                    : !note
                                )) && (
                                <span className="text-gray-500 text-xs italic">
                                  No production notes available
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

            {/* View Full Creative Library Button */}
            {productDetails.marketing_content.high_performance_creatives
              .creative_library_url && (
              <div className="text-center">
                <a
                  href={
                    productDetails.marketing_content.high_performance_creatives
                      .creative_library_url
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-white/5 hover:bg-white/10 border-2 border-[#B8460E] text-[#B8460E] px-6 py-3 rounded-xl transition-all duration-300 group"
                >
                  <Camera className="w-5 h-5" />
                  <span className="font-medium">
                    View Full Creative Library
                  </span>
                  <ArrowUpRight className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </a>
              </div>
            )}
          </div>
        )}

      {/* Know Your Customer Section - Only show if configured */}
      {productDetails?.marketing_content?.know_your_customer &&
        (productDetails.marketing_content.know_your_customer.target_demographics
          ?.length > 0 ||
          productDetails.marketing_content.know_your_customer.customer_persona
            ?.name) && (
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#B8460E]/20 rounded-full flex items-center justify-center">
                  <span className="text-[#B8460E] text-lg">👥</span>
                </div>
                <h3 className="text-2xl font-semibold text-white">
                  Know Your Customer
                </h3>
              </div>
              <span className="text-gray-400 text-sm">
                Demographics & Psychology
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Target Demographics Panel */}
              {productDetails.marketing_content.know_your_customer
                .target_demographics?.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 bg-[#B8460E]/20 rounded-full flex items-center justify-center">
                      <span className="text-[#B8460E] text-sm">📊</span>
                    </div>
                    <h4 className="text-lg font-semibold text-white">
                      Target Demographics
                    </h4>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {productDetails.marketing_content.know_your_customer.target_demographics.map(
                      (demographic, index) => (
                        <span
                          key={index}
                          className="bg-white/10 text-white px-3 py-2 rounded-full text-sm font-medium"
                        >
                          {demographic}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Meet Your Customer Panel */}
              {productDetails.marketing_content.know_your_customer
                .customer_persona?.name && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 bg-[#B8460E]/20 rounded-full flex items-center justify-center">
                      <span className="text-[#B8460E] text-sm">🎯</span>
                    </div>
                    <h4 className="text-lg font-semibold text-white">
                      Meet Your Customer: '
                      {
                        productDetails.marketing_content.know_your_customer
                          .customer_persona.name
                      }
                      '
                    </h4>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-[#B8460E]/20 rounded-full flex items-center justify-center">
                        <span className="text-[#B8460E] text-xl">👔</span>
                      </div>
                      <div>
                        <h5 className="text-white font-semibold">
                          {
                            productDetails.marketing_content.know_your_customer
                              .customer_persona.name
                          }
                        </h5>
                        <p className="text-gray-400 text-sm">
                          {productDetails.marketing_content.know_your_customer
                            .customer_persona.age &&
                            `Age ${productDetails.marketing_content.know_your_customer.customer_persona.age}`}
                          {productDetails.marketing_content.know_your_customer
                            .customer_persona.location &&
                            ` • ${productDetails.marketing_content.know_your_customer.customer_persona.location}`}
                          {productDetails.marketing_content.know_your_customer
                            .customer_persona.income &&
                            ` • ${productDetails.marketing_content.know_your_customer.customer_persona.income}`}
                          {productDetails.marketing_content.know_your_customer
                            .customer_persona.job_title &&
                            ` • ${productDetails.marketing_content.know_your_customer.customer_persona.job_title}`}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {productDetails.marketing_content.know_your_customer
                        .customer_persona.reality && (
                        <div className="flex items-start gap-3">
                          <div className="w-5 h-5 bg-[#B8460E]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                            <span className="text-[#B8460E] text-xs">💬</span>
                          </div>
                          <div>
                            <span className="text-[#B8460E] text-xs font-semibold uppercase tracking-wider">
                              Reality:
                            </span>
                            <p className="text-white text-sm">
                              {
                                productDetails.marketing_content
                                  .know_your_customer.customer_persona.reality
                              }
                            </p>
                          </div>
                        </div>
                      )}

                      {productDetails.marketing_content.know_your_customer
                        .customer_persona.fears && (
                        <div className="flex items-start gap-3">
                          <div className="w-5 h-5 bg-[#B8460E]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                            <span className="text-[#B8460E] text-xs">😰</span>
                          </div>
                          <div>
                            <span className="text-[#B8460E] text-xs font-semibold uppercase tracking-wider">
                              Fears:
                            </span>
                            <p className="text-white text-sm">
                              {
                                productDetails.marketing_content
                                  .know_your_customer.customer_persona.fears
                              }
                            </p>
                          </div>
                        </div>
                      )}

                      {productDetails.marketing_content.know_your_customer
                        .customer_persona.hook && (
                        <div className="flex items-start gap-3">
                          <div className="w-5 h-5 bg-[#B8460E]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                            <span className="text-[#B8460E] text-xs">💭</span>
                          </div>
                          <div>
                            <span className="text-[#B8460E] text-xs font-semibold uppercase tracking-wider">
                              Hook:
                            </span>
                            <p className="text-white text-sm">
                              {
                                productDetails.marketing_content
                                  .know_your_customer.customer_persona.hook
                              }
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Pain Points Section - Only show if configured */}
      {productDetails?.marketing_content?.pain_points_grid?.length > 0 && (
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
          <h3 className="text-2xl font-semibold text-white mb-6">
            Pain Points
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {productDetails.marketing_content.pain_points_grid.map(
              (painPoint, index) => (
                <div
                  key={index}
                  className="relative bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4 hover:bg-white/10 hover:border-[#B8460E]/50 hover:shadow-lg hover:shadow-[#B8460E]/20 transition-all duration-300 group overflow-hidden"
                >
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>
                  <div className="relative z-10 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center">
                        <span className="text-yellow-500 text-lg">
                          {painPoint.icon || "⚡"}
                        </span>
                      </div>
                      <h4 className="text-lg font-semibold text-white">
                        {painPoint.title}
                      </h4>
                    </div>

                    <p className="text-gray-300 text-sm italic">
                      "{painPoint.problem}"
                    </p>

                    <div className="bg-green-600/20 border border-green-600/30 rounded-lg p-3">
                      <span className="text-green-400 text-xs font-semibold uppercase tracking-wider">
                        USE THIS:
                      </span>
                      <p className="text-white text-sm font-medium mt-1">
                        {painPoint.solution}
                      </p>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Power Claims That Convert Section - Only show if configured */}
      {productDetails?.marketing_content?.power_claims?.length > 0 && (
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-6 h-6 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <span className="text-yellow-500 text-lg">⚡</span>
            </div>
            <h3 className="text-2xl font-semibold text-white">
              Power Claims That Convert
            </h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {productDetails.marketing_content.power_claims.map(
              (claim, index) => (
                <button
                  key={index}
                  className="bg-white/5 hover:bg-white/10 border-2 border-yellow-500 text-yellow-500 px-4 py-3 rounded-lg transition-all duration-300 font-semibold text-sm"
                >
                  {claim}
                </button>
              )
            )}
          </div>
        </div>
      )}

      {/* Content & Tools Access Section - Only show if configured */}
      {productDetails?.marketing_content?.content_tools_access &&
        (productDetails.marketing_content.content_tools_access
          .recharm_ugc_library?.enabled ||
          productDetails.marketing_content.content_tools_access
            .research_analysis) && (
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-6 h-6 bg-[#B8460E]/20 rounded-full flex items-center justify-center">
                <span className="text-[#B8460E] text-lg">🔧</span>
              </div>
              <h3 className="text-2xl font-semibold text-white">
                Content & Tools Access
              </h3>
              <span className="text-gray-400 text-sm ml-auto">
                Your creative resources
              </span>
            </div>

            <div className="border-t border-white/10 pt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recharm UGC Library Column */}
                {productDetails.marketing_content.content_tools_access
                  .recharm_ugc_library?.enabled && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-6 h-6 bg-[#B8460E]/20 rounded-full flex items-center justify-center">
                        <span className="text-[#B8460E] text-lg">📚</span>
                      </div>
                      <h4 className="text-lg font-semibold text-white">
                        Recharm UGC Library
                      </h4>
                    </div>

                    <div className="bg-gray-700/50 rounded-xl p-4 border border-white/10">
                      <span className="text-[#B8460E] text-sm font-semibold uppercase tracking-wider">
                        Recharm Access Instructions:
                      </span>
                      <ol className="list-decimal list-inside mt-3 space-y-2 text-white text-sm">
                        <li>
                          Visit{" "}
                          <a
                            href={
                              productDetails.marketing_content
                                .content_tools_access.recharm_ugc_library
                                .instructions?.website_url ||
                              "https://app.recharm.com/auth"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#B8460E] underline hover:text-[#8B3A0E]"
                          >
                            {productDetails.marketing_content
                              .content_tools_access.recharm_ugc_library
                              .instructions?.website_url ||
                              "app.recharm.com/auth"}
                          </a>
                        </li>
                        <li>
                          Use email:{" "}
                          {productDetails.marketing_content.content_tools_access
                            .recharm_ugc_library.instructions?.email ||
                            "ched@mailinator.com"}
                        </li>
                        <li>
                          Get 2FA code from{" "}
                          <a
                            href={
                              productDetails.marketing_content
                                .content_tools_access.recharm_ugc_library
                                .instructions?.inbox_url ||
                              "https://mailinator.com"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#B8460E] underline hover:text-[#8B3A0E]"
                          >
                            public inbox
                          </a>
                        </li>
                        <li>Enter 2FA code and you're in!</li>
                      </ol>

                      <div className="flex items-center gap-2 mt-4 p-3 bg-[#B8460E]/20 rounded-lg">
                        <span className="text-[#B8460E] text-lg">⚠️</span>
                        <span className="text-[#B8460E] text-sm font-medium">
                          Public access - don't change any account settings
                        </span>
                      </div>
                    </div>

                    <p className="text-gray-300 text-sm leading-relaxed">
                      {productDetails.marketing_content.content_tools_access
                        .recharm_ugc_library.description ||
                        "Access to 150+ verified UGC clips from real customers. Content updated weekly with fresh testimonials and usage videos."}
                    </p>
                  </div>
                )}

                {/* Research & Analysis Column */}
                {productDetails.marketing_content.content_tools_access
                  .research_analysis && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-6 h-6 bg-[#B8460E]/20 rounded-full flex items-center justify-center">
                        <span className="text-[#B8460E] text-lg">📊</span>
                      </div>
                      <h4 className="text-lg font-semibold text-white">
                        Research & Analysis
                      </h4>
                    </div>

                    <div className="space-y-4">
                      {/* Kalo Data Training */}
                      {productDetails.marketing_content.content_tools_access
                        .research_analysis.kalo_data_training && (
                        <div className="bg-gray-700/50 rounded-xl p-4 border border-white/10">
                          <h5 className="text-white font-semibold mb-2">
                            {productDetails.marketing_content
                              .content_tools_access.research_analysis
                              .kalo_data_training.title || "Kalo Data Training"}
                          </h5>
                          <p className="text-gray-300 text-sm mb-3">
                            {productDetails.marketing_content
                              .content_tools_access.research_analysis
                              .kalo_data_training.description ||
                              "Learn to analyze trending content and competitor strategies for male enhancement and natural energy supplement niches."}
                          </p>
                          <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium">
                            <span className="text-lg">📊</span>
                            {productDetails.marketing_content
                              .content_tools_access.research_analysis
                              .kalo_data_training.coming_soon
                              ? "Coming Soon"
                              : "Access Now"}
                          </button>
                        </div>
                      )}

                      {/* Competitor Intelligence */}
                      {productDetails.marketing_content.content_tools_access
                        .research_analysis.competitor_intelligence && (
                        <div className="bg-gray-700/50 rounded-xl p-4 border border-white/10">
                          <h5 className="text-white font-semibold mb-2">
                            {productDetails.marketing_content
                              .content_tools_access.research_analysis
                              .competitor_intelligence.title ||
                              "Competitor Intelligence"}
                          </h5>
                          <p className="text-gray-300 text-sm mb-3">
                            {productDetails.marketing_content
                              .content_tools_access.research_analysis
                              .competitor_intelligence.description ||
                              "Study what's working for other honey and male enhancement brands. Updated weekly with new findings."}
                          </p>
                          <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium">
                            <span className="text-lg">📊</span>
                            {productDetails.marketing_content
                              .content_tools_access.research_analysis
                              .competitor_intelligence.coming_soon
                              ? "Coming Soon"
                              : "Access Now"}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <button className="w-full bg-gray-700/50 hover:bg-gray-700/70 text-white px-4 py-3 rounded-lg transition-colors flex items-center gap-3 text-sm font-medium border border-white/10">
                        <span className="text-lg">📈</span>
                        Kalo Data Access (Coming Soon)
                      </button>

                      <button className="w-full bg-gray-700/50 hover:bg-gray-700/70 text-white px-4 py-3 rounded-lg transition-colors flex items-center gap-3 text-sm font-medium border border-white/10">
                        <span className="text-lg">🔍</span>
                        Competitor Examples (Coming Soon)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Pain Points Table */}
      {productDetails.pain_points && productDetails.pain_points.length > 0 && (
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h3 className="text-2xl font-semibold text-white flex items-center gap-3">
              <Target className="w-6 h-6 text-[#B8460E]" />
              Winning Pain Points & Triggers
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left p-4 text-white font-semibold border-r border-white/10">
                    Pain Point
                  </th>
                  <th className="text-left p-4 text-white font-semibold border-r border-white/10">
                    Psychological Trigger
                  </th>
                  <th className="text-left p-4 text-white font-semibold">
                    Ad Application
                  </th>
                </tr>
              </thead>
              <tbody>
                {productDetails.pain_points.map((point, index) => (
                  <tr
                    key={point.id || index}
                    className={`${index % 2 === 0 ? "bg-white/5" : "bg-transparent"} hover:bg-white/10 transition-colors`}
                  >
                    <td className="p-4 text-gray-300 border-r border-white/10 align-top">
                      <div className="space-y-1">
                        <div className="text-[#B8460E] text-xs font-semibold uppercase tracking-wider">
                          {point.pain_point.split(" ")[0] || "Pain Point"}
                        </div>
                        <div className="text-white text-sm leading-relaxed">
                          {point.pain_point}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-gray-300 border-r border-white/10 align-top">
                      <div className="space-y-1">
                        <div className="text-[#B8460E] text-xs font-semibold uppercase tracking-wider">
                          Trigger
                        </div>
                        <div className="text-white text-sm leading-relaxed">
                          {point.psychological_trigger}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-gray-300 align-top">
                      <div className="space-y-1">
                        <div className="text-[#B8460E] text-xs font-semibold uppercase tracking-wider">
                          Application
                        </div>
                        <div className="text-white text-sm leading-relaxed">
                          {point.ad_application}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rules Section */}
      {productDetails.rules && productDetails.rules.length > 0 && (
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
          <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-[#B8460E]" />
            Rules & Guidelines
          </h3>
          <div className="space-y-4">
            {productDetails.rules.map((rule, index) => (
              <div
                key={rule.id || index}
                className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all duration-200"
              >
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm font-bold">
                      {index + 1}
                    </span>
                  </div>
                  <p className="text-gray-300 leading-relaxed flex-1">
                    {rule.rule_text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
